/**
 * Locale-aware number helpers.
 *
 * Arabic uses Arabic-Indic digits (٠١٢٣) per the design system's locale
 * standard (`ar-SA`); English keeps Western digits.
 */

/**
 * A plain count in the reader's digits.
 * @param {Number} n
 * @param {String} language - 'ar' | 'en'
 */
export const formatCount = (n, language = 'ar') =>
  (language === 'ar' ? Number(n).toLocaleString('ar-SA') : String(n));

/**
 * A progress fraction such as "answered of total".
 *
 * Bidi note: digits joined by a slash form ONE number run, and number runs
 * always render left-to-right — so a literal "٤/١٤" shows the TOTAL on the
 * right, which an Arabic reader hits first and reads as "14 of 4". Writing the
 * operands in the opposite order is what makes the rendered result correct.
 *
 * @returns {String} "١٤/٤" (renders as ٤ then ١٤ reading right-to-left) or "4/14"
 */
export const formatProgress = (answered, total, language = 'ar') => {
  const a = formatCount(answered, language);
  const b = formatCount(total, language);
  return language === 'ar' ? `${b}/${a}` : `${a}/${b}`;
};

/**
 * A running clock of minutes and seconds — "٣٤:٠٧" / "34:07" — for the cleaning target timer
 * (tasks/cleaning-timer.md D8). Minutes are not capped at 59: a clean far past its target reads
 * "١١٢:٠٥", which is still one glance. Render it inside an LTR run so the colon and a leading
 * "+" keep their places in an RTL card.
 * @param {Number} totalSeconds - non-negative
 * @param {String} language - 'ar' | 'en'
 */
export const formatClockDuration = (totalSeconds, language = 'ar') => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const two = (n) => (language === 'ar'
    ? n.toLocaleString('ar-SA', { minimumIntegerDigits: 2, useGrouping: false })
    : String(n).padStart(2, '0'));
  return `${two(Math.floor(s / 60))}:${two(s % 60)}`;
};

/**
 * A span of whole minutes in Arabic words — "٤ ساعات", "ساعة و٣٠ دقيقة", "ساعتان", "٤٥ دقيقة".
 * For the cleaning-window line (tasks/arrival-readiness-action-side.md Phase 2 L5). Arabic count
 * agreement: 1 singular, 2 dual, 3–10 plural, 11+ singular. Arabic-only — the card it serves is.
 * @param {Number} totalMinutes - non-negative; a negative window is worded by the caller
 */
export const formatHoursMinutesAr = (totalMinutes) => {
  const total = Math.max(0, Math.round(totalMinutes));
  const unit = (n, one, two, few) => {
    if (n === 1) return one;
    if (n === 2) return two;
    return `${formatCount(n)} ${n >= 3 && n <= 10 ? few : one}`;
  };
  const h = Math.floor(total / 60);
  const m = total % 60;
  const hours = h ? unit(h, 'ساعة', 'ساعتان', 'ساعات') : '';
  const minutes = m ? unit(m, 'دقيقة', 'دقيقتان', 'دقائق') : '';
  if (hours && minutes) return `${hours} و${minutes}`;
  return hours || minutes || `${formatCount(0)} دقيقة`;
};

/**
 * A whole number typed by a person, in either digit system — the reverse of `formatCount`.
 * An Arabic phone keyboard types ٠–٩ (and Persian keyboards ۰–۹); a `type="number"` field silently
 * drops those, so count fields are text fields read through this. Returns null for anything that is
 * not a plain whole number ≥ 0 (empty, decimals, signs, letters).
 * @param {String} text
 * @returns {Number|null}
 */
export const parseWholeNumber = (text) => {
  const western = String(text ?? '')
    .trim()
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0));
  if (!/^\d+$/.test(western)) return null;
  const n = Number(western);
  return Number.isSafeInteger(n) ? n : null;
};

/**
 * A count that may be below zero — «−٢» with the sign always before the digits. `formatCount(-2)` in
 * Arabic puts a bare hyphen whose side depends on the surrounding text direction; render the result
 * inside an LTR run (`<bdi dir="ltr">`) so the sign stays put.
 */
export const formatSignedCount = (n, language = 'ar') =>
  (n < 0 ? `−${formatCount(Math.abs(n), language)}` : formatCount(n, language));

